import { EntityAudit } from "@modernary/mui/components/audit";
import { RouterView, type RouteRecordRaw, type RouterOptions } from "vue-router";
import { LE, LE_CODES } from "@/constants/enums";
import { getElectronicFormRouteName } from "@/helpers/electronicFormHelpers";
import { getLookupsFromLookupGroup } from "@modernary/mui/components/lookup";
import type { StandardFormRouteNames } from "@/views/electronicForm/electronicForm";

// Reusable HOC for dynamic imports with props
const withProps = (importFn: () => Promise<any>, defaultProps: Record<string, any> = {}) => {
  return {
    component: importFn,
    props: defaultProps,
  };
};

// Troubleshoot your routes using this - https://paths.esm.dev

const complianceForms: Array<
  keyof Pick<
    typeof LE_CODES.ElectronicForm,
    | "ef043"
    | "ef044"
    | "ef045"
    | "ef046"
    | "ef047"
    | "ef048"
    | "ef049"
    | "ef050"
    | "ef051"
    | "ef052"
    | "ef053"
    | "ef054"
    | "ef055"
    | "ef056"
    | "ef057"
    | "ef058"
    | "ef059"
  >
> = [
  "ef043",
  "ef044",
  "ef045",
  "ef046",
  "ef047",
  "ef048",
  "ef049",
  "ef050",
  "ef051",
  "ef052",
  "ef053",
  "ef054",
  "ef055",
  "ef056",
  "ef057",
  "ef058",
  "ef059",
] as const;

const nameGenerator = (acc: any, form: (typeof complianceForms)[number]) => {
  if (!acc[form]) acc[form] = {};

  const current = acc[form];
  current.new = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "New");
  current.view = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "View");
  current.edit = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Edit");
  current.base = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Base");
  current.audit = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Audit");

  current.initiateSubmission = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "InitiateSubmission");
  current.reviewSubmission = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "ReviewSubmission");
  current.repudiateSubmission = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "RepudiateSubmission");
  return acc;
};

// Explicitly defined names go here
const staticNames = {
  compliance: "ElectronicForm.Compliance",
  complianceList: "ElectronicForm.ComplianceList",
  complianceSelect: "ElectronicForm.ComplianceSelect",

  // ef001: {
  //   initiateSubmission: "ElectronicForm.ef001InitiateSubmission",
  //   reviewSubmission: "ElectronicForm.ef001ReviewSubmission",
  //   repudiateSubmission: "ElectronicForm.ef001RepudiateSubmission",
  // },
};

type StaticStationarySourceNames = typeof staticNames;

type GeneratedStationarySourceNames = {
  [K in (typeof complianceForms)[number]]: StandardFormRouteNames;
};

// Combined type for all route names -
type StationarySourceRouteNames = StaticStationarySourceNames & GeneratedStationarySourceNames;

// Dynamically generate all common names, starting with staticNames as the initial value
const names = complianceForms.reduce(nameGenerator, staticNames) as StationarySourceRouteNames;

const additionalChildRoutes: { [key: string]: RouterOptions["routes"] } = {
  // routes beyond the standard predefined routes should be added here.
  // the key should be the form code, and the value should be an array of routes.
  // ef001: [
  //   {
  //     path: "initiateSubmission",
  //     name: names.ef001.initiateSubmission,
  //     meta: { title: "Submission Agreement", isForm: true },
  //     ...withProps(() => import("@/components/submission/InitiateSubmission.vue"), {
  //       agreement: StationarySourceStandardAgreementTemplate,
  //       formRouteNames: names.ef001,
  //     }),
  //   },
  //   {
  //     path: "reviewSubmission",
  //     name: names.ef001.reviewSubmission,
  //     meta: { title: "Submission Review", isForm: true },
  //     ...withProps(() => import("@/components/submission/ReviewSubmission.vue"), {
  //       review: StationarySourceStandardReviewTemplate,
  //       formRouteNames: names.ef001,
  //     }),
  //   },
  //   {
  //     path: "repudiateSubmission",
  //     name: names.ef001.repudiateSubmission,
  //     meta: { title: "Submission Repudiation", isForm: true },
  //     ...withProps(() => import("@/components/submission/RepudiateSubmission.vue"), {
  //       formRouteNames: names.ef001,
  //     }),
  //   },
  // ],
};

const createRoutes = () => {
  const routes: RouterOptions["routes"] = complianceForms.map((form) => {
    const formRouteNames = names[form];
    const result: RouteRecordRaw = {
      path: LE_CODES.ElectronicForm[form],
      name: LE_CODES.ElectronicForm[form],
      component: {
        render: () => <RouterView />,
      },
      children: [
        {
          path: "new",
          component: () => import(`@/views/electronicForm/compliance/${LE_CODES.ElectronicForm[form]}/Base.vue`),
          redirect: { name: formRouteNames.new },
          // hard code the paths based on what form you're making...
          children: [
            {
              path: ":id(\\d+)?/:typeMurn?",
              name: formRouteNames.new,
              meta: { title: "New Form", isNew: true },
              component: () => import(`@/views/electronicForm/compliance/${LE_CODES.ElectronicForm[form]}/Edit.vue`),
            },
          ],
        },
        {
          path: ":id(\\d+)",
          name: formRouteNames.base,
          component: () => import(`@/views/electronicForm/compliance/${LE_CODES.ElectronicForm[form]}/Base.vue`),
          redirect: { name: formRouteNames.view },
          children: [
            {
              path: "view",
              name: formRouteNames.view,
              meta: { title: "Form View" },
              component: () => import(`@/views/electronicForm/compliance/${LE_CODES.ElectronicForm[form]}/View.vue`),
            },
            {
              path: "edit",
              name: formRouteNames.edit,
              meta: { title: "Form Edit", isForm: true },
              component: () => import(`@/views/electronicForm/compliance/${LE_CODES.ElectronicForm[form]}/Edit.vue`),
            },
            {
              path: "activity",
              name: formRouteNames.audit,
              meta: { title: "Form Audit Activity" },
              component: {
                render: () => <EntityAudit entityType="ElectronicForm" />,
              },
            },
            {
              path: "initiateSubmission",
              name: formRouteNames.initiateSubmission,
              meta: { title: "Submission Agreement", isForm: true },
              ...withProps(() => import("@/components/submission/InitiateSubmission.vue"), {
                formCode: form,
                formRouteNames,
              }),
            },
            {
              path: "reviewSubmission",
              name: formRouteNames.reviewSubmission,
              meta: { title: "Submission Review", isForm: true },
              ...withProps(() => import("@/components/submission/ReviewSubmission.vue"), {
                formCode: form,
                formRouteNames,
              }),
            },
            {
              path: "repudiateSubmission",
              name: formRouteNames.repudiateSubmission,
              meta: { title: "Submission Repudiation", isForm: true },
              ...withProps(() => import("@/components/submission/RepudiateSubmission.vue"), {
                formCode: form,
                formRouteNames,
              }),
            },
            ...(additionalChildRoutes[form] ? additionalChildRoutes[form] : []),
          ],
        },
      ],
    };
    return result;
  });
  return routes;
};

export { names as complianceRouteNames };

const complianceRoutes: RouterOptions["routes"] = [
  {
    path: "/compliance",
    name: names.compliance,
    component: {
      render: () => <RouterView />,
    },
    redirect: { name: names.complianceList },
    children: [
      {
        path: "list",
        name: names.complianceList,
        meta: {
          title: "Compliance Forms",
          lookupGroups: ["ElectronicForm", "ElectronicFormType", "ElectronicFormStatus"],
        },
        // props: (route) => ({
        //   filterMurns: route.params.filterMurns,
        // }),
        // beforeEnter: async (to, from, next) => {
        //   try {
        //     const formTypeLookups = await getLookupsFromLookupGroup("ElectronicForm");

        //     to.params.filterMurns = formTypeLookups._items
        //       .filter((lookup) =>
        //         lookup.metadata?.filtersOn?.groupMurn.values.includes(
        //           LE.ElectronicFormGroup.Compliance,
        //         ),
        //       )
        //       .map((x) => x.murn);

        //     next();
        //   } catch (error) {
        //     next({ name: "errorRoute" }); //TODO:
        //   }
        // },
        component: () => import("@/views/electronicForm/complianceForm/ComplianceList.vue"),
      },
      {
        path: "formSelect",
        name: names.complianceSelect,
        meta: { title: "Select Compliance Form", isNew: true },
        component: () => import("@/views/electronicForm/complianceForm/ComplianceSelect.vue"),
      },
      ...createRoutes(),
    ],
  },
];
export default complianceRoutes;
